import './LoginScreen.scss';
import {Button} from "@/components/ui/button.tsx";
import {Mail} from "lucide-react";
import GoogleLoginComponent from "@/screens/auth/google-login/GoogleLoginComponent.tsx";
import FormInput from "@/components/form-components/form-input.tsx";
import {Field, Form, Formik, FormikHelpers} from "formik";
import FormPasswordInput from "@/components/form-components/formik-password-input.tsx";
import {useNavigate} from "react-router-dom";
import {FORGOT_PASSWORD_ROUTE, SIGNUP_ROUTE} from "@/constants/RoutesConfig.tsx";
import {motion} from "framer-motion";
import {ReactNode, useCallback} from "react";
import {CommonService} from "@/services";
import {useAppDispatch} from "@/store/hooks.ts";
import {userLogin} from "@/store/actions/auth.action.ts";
import OrSeparatorComponent from "@/components/common-components/or-separator/OrSeparatorComponent.tsx";
import {buttonVariants, containerVariants, getFadeInUpVariant} from "@/constants/animations.ts";
import {useQueryClient} from "@tanstack/react-query";
import {IAPIResponseType} from "@/models/api.model.ts";
import {SignInValidationSchema} from "@/services/validations/auth.validation.ts";
import { useSocket } from '@/services/hooks/useSocket';
import {ENV} from "@/constants";
import {GoogleOAuthProvider} from "@react-oauth/google";
    
const clientId = ENV.GOOGLE_CLIENT_ID;


export interface SigninValuesType {
    email: string,
    password: string,
}

const initialValues: SigninValuesType = {
    email: '',
    password: ''
}

const SOCKET_URL = ENV.SOCKET_URL;

const SignInScreen = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const {connect} = useSocket({
        eventTypes: ['auth_success'],
        projectId: "" // Empty string for auth-related socket connection
    });



    const onLogin = useCallback((
        payload: SigninValuesType,
        {setSubmitting, setErrors}: FormikHelpers<SigninValuesType>
    ) => {
        const formattedPayload = {
            ...payload,
            email: payload.email.toLowerCase(),
        };
        
        CommonService._account.LoginAPICall(formattedPayload)
            .then((response: IAPIResponseType<any>) => {
                // Track successful login
                CommonService.handleMixpanelTrack("Login Success", {
                    user_id: response?.data?.user?.id,
                    email: response?.data?.user?.email,
                    name: response?.data?.user?.name,
                    timestamp: new Date().toISOString()
                });
                
                if (SOCKET_URL) {
                    connect(SOCKET_URL, response?.data?.token);
                }
                queryClient?.invalidateQueries();
                dispatch(userLogin(response?.data?.user, response?.data?.token, "username"));
            })
            .catch((error: IAPIResponseType<any>) => {
                // Track login failure
                CommonService.handleMixpanelTrack("Login Failed", {
                    email: formattedPayload.email,
                    error: error?.data?.error || "Unknown error",
                    timestamp: new Date().toISOString()
                });
                
                CommonService.handleErrors(setErrors, error);
            })
            .finally(() => {
                setSubmitting(false);
            })
    }, [dispatch, queryClient,connect]);


    return (
        <GoogleOAuthProvider clientId={clientId}>

        <motion.div
            className="signin-screen"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.div
                className="auth-screen-title-wrapper"
                variants={getFadeInUpVariant(0.4)}
                initial="initial"
                animate="animate"
            >
                <motion.div className="auth-screen-title">
                    Sign in
                </motion.div>
                <motion.div
                    className="auth-screen-subtitle text-secondary-foreground"
                    variants={containerVariants}
                    initial="initial"
                    animate="animate"
                >
                    Don't have an account?
                    <Button
                        type={"button"}
                        variant={"link"}
                        fullWidth={false}
                        className={"ml-[-11px] text-md"}
                        onClick={() => {
                            // Track create account button click
                            CommonService.handleMixpanelTrack("Create Account Button Clicked", {
                                timestamp: new Date().toISOString()
                            });
                            navigate(SIGNUP_ROUTE);
                        }}
                    >
                        Create one
                    </Button>
                </motion.div>
            </motion.div>
            <GoogleLoginComponent/>
            <OrSeparatorComponent/>
            <Formik
                initialValues={initialValues}
                validationSchema={SignInValidationSchema}
                validateOnBlur={false}
                enableReinitialize={false}
                validateOnChange
                validateOnMount
                onSubmit={onLogin}
            >
                {({isSubmitting}) => (
                    <Form noValidate={true}>
                        <Field name="email">
                            {(field: any) => (
                                <FormInput
                                    name="email"
                                    label="Email Address"
                                    placeholder=""
                                    icon={<Mail className="size-4 text-gray-500 peer-focus:text-blue-500"/>}
                                    formikField={field}
                                />
                            )}
                        </Field>
                        <Field name="password">
                            {(field: any) => (
                                <FormPasswordInput
                                    name="password"
                                    label="Password"
                                    type="password"
                                    placeholder=""
                                    formikField={field}
                                />
                            )}
                        </Field>
                        <motion.div variants={buttonVariants}
                                    initial="initial"
                                    animate="animate" whileHover="hover">
                            <Button
                                fullWidth
                                loading={isSubmitting}
                                onClick={() => {
                                    // Track signin button click
                                    CommonService.handleMixpanelTrack("Sign In Button Clicked", {
                                        timestamp: new Date().toISOString()
                                    });
                                }}
                            >
                                Sign In
                            </Button>
                        </motion.div>
                        <motion.div variants={buttonVariants}
                                    initial="initial"
                                    animate="animate"
                                    whileHover="hover">
                            <Button
                                fullWidth
                                type={"button"}
                                variant={"link"}
                                onClick={() => {
                                    // Track forgot password button click
                                    CommonService.handleMixpanelTrack("Forgot Password Button Clicked", {
                                        timestamp: new Date().toISOString()
                                    });
                                    navigate(FORGOT_PASSWORD_ROUTE);
                                }}
                            >
                                Forgot Password
                            </Button>
                        </motion.div>
                    </Form>
                ) as ReactNode}
            </Formik>
        </motion.div>
        </GoogleOAuthProvider> as ReactNode
    );
};

export default SignInScreen;
